import { useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Stack,
  Avatar,
  Badge,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  IoTime,
  IoWarning,
  IoCheckmarkCircle,
  IoEllipsisVertical,
  IoRefresh,
  IoSend,
  IoHammer,
  IoEye,
  IoAlertCircle,
  IoCheckmark,
  IoClose,
} from 'react-icons/io5';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, format } from 'date-fns';

import { transactionService } from 'src/services/transaction.service';
import { studentService } from 'src/services/student.service';
import { ToastNotifications } from 'src/components';
import { Tables } from 'src/types/db.types';

interface TransactionStatusTrackerProps {
  teacherId: string;
  classIds?: string[];
}

type TransactionWithStudent = Tables<'transactions'> & {
  transaction_type: Tables<'transaction_types'>;
  student: {
    id: string;
    user: {
      firstname: string;
      lastname: string;
    };
  };
};

type StatusFilter = 'all' | 'PENDING_STUDENT_ACTION' | 'DEFERRED' | 'OVERDUE' | 'QUESTIONED';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transaction-tabpanel-${index}`}
      aria-labelledby={`transaction-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

export default function TransactionStatusTracker({ teacherId, classIds }: TransactionStatusTrackerProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideAction, setOverrideAction] = useState<'approve' | 'cancel'>('approve');
  const [tabValue, setTabValue] = useState(0);

  const queryClient = useQueryClient();

  // Fetch all pending transactions for teacher's students
  const {
    data: pendingTransactions = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['teacher-pending-transactions', teacherId, classIds],
    queryFn: async () => {
      return await transactionService.getPendingTransactionsForTeacher(teacherId, classIds);
    },
    enabled: !!teacherId,
  });

  // Get transaction summary statistics
  const transactionStats = pendingTransactions.reduce(
    (
      acc: { pending: number; deferred: number; overdue: number; questioned: number; total: number },
      transaction: TransactionWithStudent
    ) => {
      if (transaction.transaction_status === 'PENDING_STUDENT_ACTION') {
        acc.pending += 1;
      } else if (transaction.transaction_status === 'DEFERRED') {
        if (transaction.due_date && new Date(transaction.due_date) < new Date()) {
          acc.overdue += 1;
        } else {
          acc.deferred += 1;
        }
      } else if (transaction.transaction_status === 'QUESTIONED') {
        acc.questioned += 1;
      }
      return acc;
    },
    { pending: 0, deferred: 0, overdue: 0, questioned: 0, total: 0 }
  );

  transactionStats.total =
    transactionStats.pending + transactionStats.deferred + transactionStats.overdue + transactionStats.questioned;

  // Filter transactions based on selected filter
  const filteredTransactions = pendingTransactions.filter((transaction: TransactionWithStudent) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'OVERDUE') {
      return (
        transaction.transaction_status === 'DEFERRED' &&
        transaction.due_date &&
        new Date(transaction.due_date) < new Date()
      );
    }
    return transaction.transaction_status === statusFilter;
  });

  // Override transaction mutation
  const overrideTransactionMutation = useMutation({
    mutationFn: ({
      transactionId,
      action,
      reason,
    }: {
      transactionId: string;
      action: 'approve' | 'cancel';
      reason: string;
    }) => transactionService.overrideTransaction(transactionId, teacherId, action, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher-pending-transactions'] });
      ToastNotifications.success('Transaction override applied successfully!');
      handleCloseOverrideModal();
    },
    onError: (error: any) => {
      ToastNotifications.error(error.message || 'Failed to override transaction');
    },
  });

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, transactionId: string) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedTransactionId(transactionId);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    // Don't clear selectedTransactionId here - let the modal handle it
  };

  const handleOverrideTransaction = (action: 'approve' | 'cancel') => {
    setOverrideAction(action);
    setIsOverrideModalOpen(true);
    handleActionMenuClose();
  };

  const handleCloseOverrideModal = () => {
    setIsOverrideModalOpen(false);
    setOverrideReason('');
    setSelectedTransactionId(null);
  };

  const handleConfirmOverride = () => {
    if (!selectedTransactionId) {
      ToastNotifications.error('No transaction selected');
      return;
    }

    if (!overrideReason.trim()) {
      ToastNotifications.error('Please provide a reason for the override');
      return;
    }

    overrideTransactionMutation.mutate({
      transactionId: selectedTransactionId,
      action: overrideAction,
      reason: overrideReason.trim(),
    });
  };

  const handleSendNudge = async () => {
    if (!selectedTransactionId) return;

    try {
      await transactionService.sendStudentReminder(selectedTransactionId, teacherId);
      ToastNotifications.success('Reminder sent to student!');
      handleActionMenuClose();
      setSelectedTransactionId(null); // Clear after successful action
    } catch (error: any) {
      ToastNotifications.error(error.message || 'Failed to send reminder');
    }
  };

  const getStatusColor = (transaction: TransactionWithStudent) => {
    if (transaction.transaction_status === 'PENDING_STUDENT_ACTION') {
      return 'warning';
    }
    if (transaction.transaction_status === 'DEFERRED') {
      if (transaction.due_date && new Date(transaction.due_date) < new Date()) {
        return 'error'; // Overdue
      }
      return 'info'; // Deferred but not overdue
    }
    if (transaction.transaction_status === 'QUESTIONED') {
      return 'secondary';
    }
    return 'default';
  };

  const getStatusLabel = (transaction: TransactionWithStudent) => {
    if (transaction.transaction_status === 'PENDING_STUDENT_ACTION') {
      return 'Awaiting Student';
    }
    if (transaction.transaction_status === 'DEFERRED') {
      if (transaction.due_date && new Date(transaction.due_date) < new Date()) {
        return 'Overdue';
      }
      return `Due ${formatDistanceToNow(new Date(transaction.due_date!), { addSuffix: true })}`;
    }
    if (transaction.transaction_status === 'QUESTIONED') {
      return 'Student Question';
    }
    return transaction.transaction_status;
  };

  const getUrgencyLevel = (transaction: TransactionWithStudent): 'low' | 'medium' | 'high' => {
    if (transaction.transaction_status === 'DEFERRED' && transaction.due_date) {
      const daysUntilDue = Math.ceil((new Date(transaction.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysUntilDue < 0) return 'high'; // Overdue
      if (daysUntilDue <= 1) return 'high';
      if (daysUntilDue <= 3) return 'medium';
    }
    return 'low';
  };

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error Loading Transaction Data</AlertTitle>
        Unable to load transaction status information. Please try refreshing.
      </Alert>
    );
  }

  return (
    <Card sx={{ borderRadius: 3 }}>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            Transaction Status Tracker
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh data">
              <IconButton onClick={() => refetch()} size="small">
                <IoRefresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Status Summary Cards */}
        <Box display="flex" gap={2} mb={3}>
          <Card sx={{ p: 2, flex: 1, bgcolor: 'warning.50', borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Badge badgeContent={transactionStats.pending} color="warning">
                <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                  <IoTime />
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="h6">{transactionStats.pending}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Pending Response
                </Typography>
              </Box>
            </Box>
          </Card>

          <Card sx={{ p: 2, flex: 1, bgcolor: 'info.50', borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Badge badgeContent={transactionStats.deferred} color="info">
                <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                  <IoAlertCircle />
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="h6">{transactionStats.deferred}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Deferred
                </Typography>
              </Box>
            </Box>
          </Card>

          <Card sx={{ p: 2, flex: 1, bgcolor: 'error.50', borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Badge badgeContent={transactionStats.overdue} color="error">
                <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>
                  <IoWarning />
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="h6">{transactionStats.overdue}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Overdue
                </Typography>
              </Box>
            </Box>
          </Card>

          <Card sx={{ p: 2, flex: 1, bgcolor: 'purple.50', borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Badge badgeContent={transactionStats.questioned} color="secondary">
                <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                  <IoAlertCircle />
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="h6">{transactionStats.questioned}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Questioned
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Filter Tabs */}
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
          <Tab label={`All (${transactionStats.total})`} onClick={() => setStatusFilter('all')} />
          <Tab
            label={`Pending (${transactionStats.pending})`}
            onClick={() => setStatusFilter('PENDING_STUDENT_ACTION')}
          />
          <Tab label={`Deferred (${transactionStats.deferred})`} onClick={() => setStatusFilter('DEFERRED')} />
          <Tab label={`Overdue (${transactionStats.overdue})`} onClick={() => setStatusFilter('OVERDUE')} />
          <Tab label={`Questioned (${transactionStats.questioned})`} onClick={() => setStatusFilter('QUESTIONED')} />
        </Tabs>

        {/* Transactions Table */}
        <TabPanel value={tabValue} index={tabValue}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              Loading transactions...
            </Box>
          ) : filteredTransactions.length === 0 ? (
            <Box textAlign="center" py={4}>
              <IoCheckmarkCircle size={48} color="#4caf50" />
              <Typography variant="h6" mt={2}>
                All Caught Up!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No transactions require your attention in this category.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Transaction</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.map((transaction: TransactionWithStudent) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                            {transaction.student.user.firstname[0]}
                            {transaction.student.user.lastname[0]}
                          </Avatar>
                          <Typography variant="body2">
                            {transaction.student.user.firstname} {transaction.student.user.lastname}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {transaction.transaction_type.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {transaction.description || transaction.reason}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          color={transaction.transaction_type.type === 'debit' ? 'error.main' : 'success.main'}
                          fontWeight="medium"
                        >
                          {transaction.transaction_type.type === 'debit' ? '-' : '+'}${transaction.amount}
                          {transaction.late_fee_total > 0 && (
                            <Typography variant="caption" color="error.main" display="block">
                              +${transaction.late_fee_total} late fee
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={getStatusLabel(transaction)}
                          color={getStatusColor(transaction) as any}
                          size="small"
                          icon={getUrgencyLevel(transaction) === 'high' ? <IoWarning /> : undefined}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(transaction.createdat), 'MMM dd, yyyy')}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <IconButton size="small" onClick={(e) => handleActionMenuOpen(e, transaction.id)}>
                          <IoEllipsisVertical />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Box>

      {/* Action Menu */}
      <Menu anchorEl={actionMenuAnchor} open={Boolean(actionMenuAnchor)} onClose={handleActionMenuClose}>
        <MenuItem onClick={() => handleSendNudge()}>
          <IoSend style={{ marginRight: 8 }} />
          Send Reminder
        </MenuItem>
        <MenuItem onClick={() => handleOverrideTransaction('approve')}>
          <IoCheckmark style={{ marginRight: 8 }} />
          Force Approve
        </MenuItem>
        <MenuItem onClick={() => handleOverrideTransaction('cancel')}>
          <IoClose style={{ marginRight: 8 }} />
          Cancel Transaction
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          handleActionMenuClose();
          setSelectedTransactionId(null);
        }}>
          <IoEye style={{ marginRight: 8 }} />
          View Details
        </MenuItem>
      </Menu>

      {/* Override Modal */}
      <Dialog
        open={isOverrideModalOpen}
        onClose={handleCloseOverrideModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Typography variant="h6">{overrideAction === 'approve' ? 'Force Approve' : 'Cancel'} Transaction</Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            <Alert severity="warning">
              <AlertTitle>Teacher Override</AlertTitle>
              This action will override the student's decision and cannot be undone. Please provide a clear reason for
              this override.
            </Alert>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for Override"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Explain why you're overriding this transaction..."
            />

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={handleCloseOverrideModal}>Cancel</Button>
              <Button
                variant="contained"
                color={overrideAction === 'approve' ? 'success' : 'error'}
                onClick={handleConfirmOverride}
                disabled={!overrideReason.trim() || overrideTransactionMutation.isPending}
                startIcon={<IoHammer />}
              >
                {overrideAction === 'approve' ? 'Force Approve' : 'Cancel Transaction'}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
